import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { fechaHora } from '@/lib/formato';

import {
  CATEGORIAS,
  MODOS,
  nombreEspecialidad,
  useEspecialidades,
  useExamenes,
  type ExamenDetalle,
} from './queries';

type Body = components['schemas']['ExamCreateBody'];
const CLAVES = ['q'] as const;

function FormExamen({ onListo }: { onListo: (id: number) => void }) {
  const esp = useEspecialidades();
  const [f, setF] = useState<Body>({ name: '', mode: 'enarm', category: 'enarm', count: 100 });
  const [dist, setDist] = useState<{ specialtyId: number; count: number }[]>([
    { specialtyId: 0, count: 10 },
  ]);
  const [ids, setIds] = useState('');
  const crear = useMutation({
    mutationFn: () => {
      const body: Body = { name: f.name, mode: f.mode, category: f.category };
      if (f.category === 'enarm') body.count = f.count;
      if (f.category === 'by_specialty')
        body.distribution = dist.filter((d) => d.specialtyId > 0 && d.count > 0);
      if (f.category === 'custom')
        body.questionIds = ids
          .split(/[\s,]+/)
          .map(Number)
          .filter((n) => n > 0);
      return unwrap<ExamenDetalle>(api.POST('/api/v1/admin/exams', { body }));
    },
    onSuccess: (e) => {
      toast.success(`Examen creado con ${e.questionCount} casos.`);
      onListo(e.id);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        crear.mutate();
      }}
    >
      <Campo etiqueta="Nombre">
        <input
          className="input-vidrio"
          required
          minLength={3}
          maxLength={50}
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
      </Campo>
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Modo">
          <select
            className="input-vidrio"
            value={f.mode}
            onChange={(e) => setF({ ...f, mode: e.target.value as Body['mode'] })}
          >
            {Object.entries(MODOS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Cómo elegir los casos">
          <select
            className="input-vidrio"
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value as Body['category'] })}
          >
            <option value="enarm">Reparto ENARM (20/25/30/25 %)</option>
            <option value="by_specialty">Por especialidad</option>
            <option value="custom">Personalizado (IDs)</option>
          </select>
        </Campo>
      </div>
      {f.category === 'enarm' && (
        <Campo
          etiqueta="Número de casos"
          ayuda="Al azar: 20 % Cirugía, 25 % Ginecología, 30 % Medicina interna y 25 % Pediatría. 350 o más es un simulacro."
        >
          <input
            type="number"
            min={4}
            max={1000}
            className="input-vidrio max-w-40"
            value={f.count ?? 100}
            onChange={(e) => setF({ ...f, count: Number(e.target.value) })}
          />
        </Campo>
      )}
      {f.category === 'by_specialty' && (
        <div className="space-y-2">
          {dist.map((d, i) => (
            <div key={i} className="flex gap-2">
              <select
                className="input-vidrio"
                value={d.specialtyId || ''}
                onChange={(e) =>
                  setDist(
                    dist.map((x, j) =>
                      j === i ? { ...x, specialtyId: Number(e.target.value) } : x,
                    ),
                  )
                }
                aria-label="Especialidad"
              >
                <option value="">Especialidad…</option>
                {esp.data?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {nombreEspecialidad(e)} ({e.questions})
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                className="input-vidrio max-w-28"
                value={d.count}
                aria-label="Casos"
                onChange={(e) =>
                  setDist(
                    dist.map((x, j) => (j === i ? { ...x, count: Number(e.target.value) } : x)),
                  )
                }
              />
              <button
                type="button"
                className="rounded-full p-2 hover:bg-[var(--pista)]"
                aria-label="Quitar"
                onClick={() => setDist(dist.filter((_x, j) => j !== i))}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-cs btn-contorno"
            onClick={() => setDist([...dist, { specialtyId: 0, count: 10 }])}
          >
            <Plus size={16} aria-hidden /> Agregar especialidad
          </button>
        </div>
      )}
      {f.category === 'custom' && (
        <Campo etiqueta="IDs de los casos (separados por coma o espacio)">
          <textarea
            className="input-vidrio min-h-24 font-mono"
            value={ids}
            onChange={(e) => setIds(e.target.value)}
          />
        </Campo>
      )}
      <div className="flex justify-end">
        <button type="submit" className="btn-cs btn-primario" disabled={crear.isPending}>
          {crear.isPending ? 'Creando…' : 'Crear examen'}
        </button>
      </div>
    </form>
  );
}

function Examenes() {
  const { filtros, cambiar } = useFiltrosUrl(CLAVES);
  const editable = useCan('preguntas', 'editar');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [nuevo, setNuevo] = useState(false);
  const q = useExamenes({ page: filtros.page, ...(filtros.q && { q: filtros.q }) });
  return (
    <>
      <Encabezado
        titulo="Exámenes y simulacros"
        acciones={
          editable && (
            <button type="button" className="btn-cs btn-primario" onClick={() => setNuevo(true)}>
              <Plus size={16} aria-hidden /> Nuevo examen
            </button>
          )
        }
      />
      <input
        className="input-vidrio mb-4 max-w-xs"
        placeholder="Buscar por nombre"
        defaultValue={filtros.q}
        onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value.trim() })}
        onBlur={(e) => cambiar({ q: e.target.value.trim() })}
        aria-label="Buscar"
      />
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin exámenes" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Examen</th>
                <th>Casos</th>
                <th>Selección</th>
                <th>Modo</th>
                <th>Creado</th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Link to={`/preguntas/examenes/${e.id}`} className="font-bold">
                      {e.name}
                    </Link>
                    {e.questionCount >= 350 && <span className="insignia ml-1">Simulacro</span>}
                  </td>
                  <td className="tabular">{e.questionCount}</td>
                  <td>{CATEGORIAS[e.category ?? ''] ?? e.category ?? '—'}</td>
                  <td>{e.mode === 'entrenamiento' ? 'Entrenamiento' : 'ENARM'}</td>
                  <td className="whitespace-nowrap">{fechaHora(e.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      <Modal
        abierto={nuevo}
        onCerrar={() => setNuevo(false)}
        titulo="Nuevo examen"
        ancho="max-w-2xl"
      >
        <FormExamen
          onListo={(id) => {
            setNuevo(false);
            void qc.invalidateQueries({ queryKey: ['examenes', 'lista'] });
            void navigate(`/preguntas/examenes/${id}`);
          }}
        />
      </Modal>
    </>
  );
}

export function ExamenesPage() {
  return (
    <PermissionGate seccion="preguntas">
      <Examenes />
    </PermissionGate>
  );
}
